import React, { useState } from 'react';
import { Typography as MuiTypography, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Input } from 'components/ui';
import type { QuestionInputProps } from '../types/questionnaire';

/**
 * Shared text input component for questionnaires
 * Handles: question_open, question_text, question_numeric
 */
export const TextInput: React.FC<QuestionInputProps> = ({
  question,
  value,
  onChange,
  disabled = false,
  error,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const [localError, setLocalError] = useState<string | null>(null);

  const isNumeric = question.questionType?.toLowerCase() === 'question_numeric';

  const validateNumeric = (val: string) => {
    if (!question.minValue && !question.maxValue) return;
    const numValue = parseFloat(val);
    if (
      question.minValue !== undefined &&
      numValue < parseFloat(String(question.minValue))
    ) {
      setLocalError('Input value is not correct');
      return;
    }
    if (
      question.maxValue !== undefined &&
      numValue > parseFloat(String(question.maxValue))
    ) {
      setLocalError('Input value is not correct');
      return;
    }
    setLocalError(null);
  };

  const validateOpen = (val: string) => {
    if (!question.characterLimit && !question.wordLimit) return;
    if (question.wordLimit && val.split(' ').length > question.wordLimit) {
      setLocalError(
        t('Maximum number of words is {{words}}', {
          words: question.wordLimit,
        })
      );
      return;
    }
    if (question.characterLimit && val.length > question.characterLimit) {
      setLocalError(
        t('Maximum number of characters is {{characters}}', {
          characters: question.characterLimit,
        })
      );
      return;
    }
    setLocalError(null);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    if (isNumeric) {
      validateNumeric(newValue);
    } else if (question.questionType?.toLowerCase() === 'question_open') {
      validateOpen(newValue);
    }
    onChange(newValue);
  };

  return (
    <>
      {/* Validation hints for numeric questions */}
      {isNumeric &&
        (parseFloat(String(question.minValue)) ||
          parseFloat(String(question.minValue)) === 0 ||
          parseFloat(String(question.maxValue)) ||
          parseFloat(String(question.maxValue)) === 0) && (
          <div
            style={{
              display: 'flex',
              gap: 5,
              backgroundColor: theme.palette.colors.gray200,
              padding: 5,
              borderRadius: 6,
              width: 'fit-content',
              marginBottom: '5px',
            }}
          >
            <MuiTypography variant="subtitle1">
              {(parseFloat(String(question.minValue)) ||
                parseFloat(String(question.minValue)) === 0) &&
              (parseFloat(String(question.maxValue)) ||
                parseFloat(String(question.maxValue)) === 0)
                ? `${t('Answer within range')}: ${question.minValue} - ${
                    question.maxValue
                  }`
                : parseFloat(String(question.minValue)) ||
                  parseFloat(String(question.minValue)) === 0
                ? `${t('Answer must be greater than')}: ${question.minValue}`
                : `${t('Answer must be smaller than')}: ${question.maxValue}`}
            </MuiTypography>
          </div>
        )}

      {/* Character/Word limit hints */}
      {(question.characterLimit || question.wordLimit) && (
        <div
          style={{
            display: 'flex',
            gap: 5,
            backgroundColor: theme.palette.colors.gray200,
            padding: 5,
            borderRadius: 6,
            width: 'fit-content',
            marginBottom: '5px',
          }}
        >
          <MuiTypography variant="subtitle1">
            {question.characterLimit
              ? t('Maximum number of characters is {{characters}}', {
                  characters: question.characterLimit,
                })
              : null}
            {question.characterLimit && question.wordLimit ? ' · ' : null}
            {question.wordLimit
              ? t('Maximum number of words is {{words}}', {
                  words: question.wordLimit,
                })
              : null}
          </MuiTypography>
        </div>
      )}

      <Input
        value={value || ''}
        onChange={handleChange}
        error={!!localError || !!error}
        onKeyDown={
          isNumeric
            ? (evt) =>
                ['e', 'E', '+', ','].includes(evt.key) && evt.preventDefault()
            : undefined
        }
        style={{
          backgroundColor: disabled ? '#f5f5f5' : theme.palette.colors.white,
          opacity: disabled ? 0.8 : 1,
        }}
        type={isNumeric ? 'number' : 'text'}
        inputProps={{ maxLength: question.characterLimit }}
        disabled={disabled}
      />

      {(localError || error) && (
        <p style={{ fontSize: 12, color: theme.palette.colors.red100 }}>
          {t(localError || error || '')}
        </p>
      )}
    </>
  );
};
